import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertTriangle,
  CheckCircle2,
  Coins,
  Link2,
  Link2Off,
  ShieldCheck,
} from "lucide-react";
import PasswordInput from "@/components/auth/PasswordInput";
import FxMarketPill from "@/components/FxMarketPill";
import {
  oandaAccount,
  oandaConnect,
  oandaDisconnect,
  oandaStatus,
  formatApiError,
  toastApiError,
} from "@/lib/api";

const fmtMoney = (v, ccy = "USD") => {
  if (v == null) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency", currency: ccy, maximumFractionDigits: 2,
  });
};

export default function OandaSettingsPage() {
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [accountId, setAccountId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const s = await oandaStatus();
      setStatus(s);
      if (s?.connected) {
        try {
          setAccount(await oandaAccount());
        } catch (e) {
          setAccount({ _error: formatApiError(e) });
        }
      } else {
        setAccount(null);
      }
    } catch (e) {
      toastApiError(e, "OANDA status failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const trimmedToken = token.trim();
      const trimmedAccount = accountId.trim();
      if (!trimmedToken || !trimmedAccount) {
        toast.error("Both token and account ID are required.");
        return;
      }
      const res = await oandaConnect({
        token: trimmedToken,
        account_id: trimmedAccount,
      });
      toast.success(
        `OANDA practice connected — account ${res.account_id}`,
      );
      setToken("");
      setAccountId("");
      await refresh();
    } catch (err) {
      toastApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        "Disconnect OANDA? Your OANDA Practice portfolio and its trade "
        + "history in NUMIROCK will be removed. Your actual OANDA demo "
        + "account is untouched — close positions there separately if needed.",
      )
    ) {
      return;
    }
    setSubmitting(true);
    try {
      await oandaDisconnect();
      toast.success("OANDA disconnected.");
      await refresh();
    } catch (err) {
      toastApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="oanda-settings-loading"
        className="py-16 text-center text-sm text-[var(--aurion-muted)]"
      >
        Loading OANDA status…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="oanda-settings-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[var(--aurion-gold)]">
            OANDA v20 · Practice
          </h1>
          <p className="mt-1 text-sm text-[var(--aurion-muted)]">
            Connect your OANDA <strong>practice / fxDemo</strong> account to
            trade real spot gold, silver, and major FX pairs (EUR/USD, GBP/USD,
            USD/JPY) — all on demo money. NUMIROCK's OANDA base URL is
            hard-coded to{" "}
            <code className="text-[var(--aurion-text-dim)]">
              api-fxpractice.oanda.com
            </code>
            {" "}so live tokens cannot ever fire — they will simply fail to
            authenticate.
          </p>
        </div>
        <FxMarketPill testid="oanda-market-pill" />
      </header>

      <Alert
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
        data-testid="oanda-security-note"
      >
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Your token never leaves the server as plain text</AlertTitle>
        <AlertDescription className="text-xs text-[var(--aurion-muted)]">
          The personal access token is Fernet-encrypted at rest and only
          reconstituted in-memory during a request. NUMIROCK never returns
          the token in any API response — even to master accounts in cloak
          mode. Account IDs must match the practice format
          <code className="mx-1">101-XXX-XXXXXXX-XXX</code> — live IDs
          (<code>001-…</code>) are refused locally before any network call.
        </AlertDescription>
      </Alert>

      {status?.connected ? (
        <Card
          className="border-emerald-500/30 bg-[var(--aurion-panel)]/60"
          data-testid="oanda-connected-card"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <CardTitle className="text-base uppercase tracking-widest text-emerald-300">
              Connected · Practice
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Token"
              value={`•••• •••• •••• ${status.token_last4 || "????"}`}
              testid="oanda-token-last4"
            />
            <Field label="Account ID" value={status.account_id} testid="oanda-account-id" />
            <Field label="Alias" value={status.alias || "—"} testid="oanda-alias" />
            <Field label="Currency" value={status.currency || "USD"} testid="oanda-currency" />
            {account && !account._error ? (
              <>
                <Field
                  label="NAV"
                  value={fmtMoney(account.nav, account.currency)}
                  testid="oanda-nav"
                />
                <Field
                  label="Balance"
                  value={fmtMoney(account.balance, account.currency)}
                  testid="oanda-balance"
                />
                <Field
                  label="Margin available"
                  value={fmtMoney(account.margin_available, account.currency)}
                  testid="oanda-margin-available"
                />
                <Field
                  label="Open positions"
                  value={account.open_position_count ?? 0}
                  testid="oanda-positions-count"
                />
              </>
            ) : account?._error ? (
              <div className="col-span-full">
                <Alert variant="destructive" data-testid="oanda-account-error">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>OANDA account read failed</AlertTitle>
                  <AlertDescription>{account._error}</AlertDescription>
                </Alert>
              </div>
            ) : null}
            <div className="col-span-full">
              <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Verified instruments
              </div>
              <div
                className="mt-1 flex flex-wrap gap-1 text-[10px] font-mono"
                data-testid="oanda-instrument-map"
              >
                {Object.entries(status.instruments || {}).map(([name, meta]) => (
                  <span
                    key={name}
                    className="rounded-sm border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-1.5 py-[1px] text-[var(--aurion-text-dim)]"
                    data-testid={`oanda-instrument-${name}`}
                  >
                    {name} · precision {meta?.trade_units_precision ?? 0}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-span-full flex justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                data-testid="oanda-disconnect-btn"
                onClick={disconnect}
                disabled={submitting}
                className="border-[var(--aurion-red)]/40 text-[var(--aurion-red)] hover:bg-[var(--aurion-red)]/10"
              >
                <Link2Off className="mr-2 h-4 w-4" />
                Disconnect OANDA
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card
          className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
          data-testid="oanda-connect-card"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <Link2 className="h-5 w-5 text-[var(--aurion-gold)]" />
            <CardTitle className="text-base uppercase tracking-widest text-[var(--aurion-gold)]">
              Connect a practice account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4" autoComplete="off">
              <p className="text-xs text-[var(--aurion-muted)]">
                Generate a personal access token at{" "}
                <a
                  href="https://www.oanda.com/demo-account/tpa/personal_token"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  OANDA → Manage API access
                </a>
                . Practice-only. If your region routes to OANDA Global Markets
                (MT5-only), personal-access tokens are not available; contact
                OANDA support to switch to a v20 practice account.
              </p>
              <div>
                <Label
                  htmlFor="oanda-token-input"
                  className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
                >
                  Personal access token
                </Label>
                <PasswordInput
                  id="oanda-token-input"
                  data-testid="oanda-token-input"
                  autoComplete="off"
                  spellCheck={false}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="e.g. 8a…-3c…"
                  className="font-mono text-sm"
                  toggleTestId="oanda-token-toggle"
                />
              </div>
              <div>
                <Label
                  htmlFor="oanda-account-input"
                  className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
                >
                  Practice account ID
                </Label>
                <Input
                  id="oanda-account-input"
                  data-testid="oanda-account-input"
                  autoComplete="off"
                  spellCheck={false}
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  placeholder="101-XXX-XXXXXXX-XXX"
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="submit"
                  data-testid="oanda-connect-btn"
                  disabled={submitting}
                  className="bg-[var(--aurion-gold)] text-black hover:bg-[var(--aurion-gold)]/90"
                >
                  {submitting ? "Connecting…" : "Connect practice"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Alert
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
        data-testid="oanda-demo-only"
      >
        <Coins className="h-4 w-4 text-[var(--aurion-gold)]" />
        <AlertTitle>Practice money only — by design</AlertTitle>
        <AlertDescription className="text-xs text-[var(--aurion-muted)]">
          v1 supports opening LONG positions on XAU/USD, XAG/USD, EUR/USD,
          GBP/USD and USD/JPY, and reduce-only closes of those longs.
          Shorting is disabled in the executor. FX submits integer base-
          currency units (e.g. 1000 EUR long EUR/USD); metals use the
          precision returned by <code>/v3/accounts/…/instruments</code>.
          Weekend orders return HTTP 200 with{" "}
          <code>orderCancelTransaction.reason=MARKET_HALTED</code> — NUMIROCK
          surfaces that cleanly to the user.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm text-[var(--aurion-text)]" data-testid={testid}>
        {value ?? "—"}
      </div>
    </div>
  );
}
